import { ArrowLeft } from 'lucide-react'
import { T } from '../theme/theme'

/**
 * BackButton — shared "go back" control for page header bars.
 *
 * Design fix (Aug 2026): every page header used to render its own
 * unstyled `<button>` containing a literal "← Back to X" string — no
 * border, no padding, no background, no hover state — sitting directly
 * next to the KaiLux logo in the top-left of the header bar. It read as
 * a stray line of text rather than a real control, out of place next to
 * every other properly-styled button in the app.
 *
 * This renders a proper bordered pill button with a real ArrowLeft icon
 * (the "←" glyph renders inconsistently across fonts/platforms) and a
 * hover state, matching the app's other secondary/outline buttons (e.g.
 * Notes.jsx tab buttons, Review.jsx "Skip review").
 *
 * Colors default to the shared theme tokens (src/theme/theme.js) and can
 * still be overridden per page — Statements.jsx (a light page with a dark
 * header bar) passes its own until it is migrated.
 */
export default function BackButton({
  onClick,
  label = 'Back',
  textColor = T.mt,
  borderColor = T.bd,
  accentColor = T.or,
  style = {},
}) {
  return (
    <button
      onClick={onClick}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = accentColor
        e.currentTarget.style.color = accentColor
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = borderColor
        e.currentTarget.style.color = textColor
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        background: 'transparent',
        border: `1px solid ${borderColor}`,
        borderRadius: '7px',
        padding: '6px 12px 6px 10px',
        color: textColor,
        fontFamily: "'Outfit',sans-serif",
        fontSize: '13px',
        fontWeight: '600',
        lineHeight: 1,
        cursor: 'pointer',
        transition: 'border-color 0.15s ease, color 0.15s ease',
        flexShrink: 0,
        ...style,
      }}
    >
      <ArrowLeft size={15} strokeWidth={2.25} />
      <span>{label}</span>
    </button>
  )
}
